<template>
    <div class="modern-timeline">
        <div class="timeline-node" v-for="stepItem in steps" :key="stepItem.rank">
            <div class="node-left">
                <div class="node-indicator" :class="stepItem.active ? 'bg-primary-theme' : stepItem.status">
                    <i :class="stepItem.active ? 'ri-play-fill' : getTimelineIcon(stepItem.status)"></i>
                </div>
                <div class="node-line" v-if="stepItem.rank < steps.length"></div>
            </div>
            <div class="node-right shadow-xs rounded-lg p-3 border mb-3" :class="stepItem.active ? 'border-primary-theme shadow-sm' : ''">
                <div class="d-flex justify-content-between align-items-start flex-wrap">
                    <div>
                        <span class="node-rank">{{ $t('approve_step') }} #{{ stepItem.rank }}</span>
                        <h5 class="node-title mb-2 mt-1">{{ stepItem.name }}</h5>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-badge :variant="stepItem.active ? 'primary' : getBadgeVariant(stepItem.status)" class="px-3 py-1 badge-pill text-uppercase mr-2 font-weight-bold">
                            {{ stepItem.status_name }}
                        </b-badge>
                    </div>
                </div>

                <div class="row pt-2 mt-2 border-top text-muted small">
                    <div class="col-sm-4 mb-2 mb-sm-0">
                        <span class="d-block font-weight-bold text-dark">{{ $t('can_approvers') }}</span>
                        <span v-for="(approver, index) in stepItem.approvers" :key="index" class="d-block mt-1">
                            <i class="ri-user-line mr-1"></i>{{ approver.name }} {{ approver.surname }}
                        </span>
                    </div>
                    <div class="col-sm-4 mb-2 mb-sm-0" v-if="stepItem.approved && stepItem.approved.length">
                        <span class="d-block font-weight-bold text-dark">{{ $t('approved_by') }}</span>
                        <span v-for="(approved, index) in stepItem.approved" :key="index" class="d-block mt-1">
                            <i class="ri-checkbox-circle-line mr-1 text-success"></i>{{ approved.name }} {{ approved.surname }}
                        </span>
                    </div>
                    <div class="col-sm-4" v-if="stepItem.updated_at">
                        <span class="d-block font-weight-bold text-dark">{{ $t('updated_at') }}</span>
                        <span class="d-block mt-1"><i class="ri-time-line mr-1"></i>{{ stepItem.updated_at }}</span>
                    </div>
                </div>

                <div class="mt-3 p-3 bg-light rounded border-left-danger" v-if="stepItem.reason">
                    <span class="d-block font-weight-bold text-danger text-uppercase small mb-1">{{ $t('explanation') }}</span>
                    <p class="mb-0 text-dark font-italic">"{{ stepItem.reason }}"</p>
                </div>

                <!-- Inline Action Form (Status Multiselect & Save Button) -->
                <div class="mt-3 pt-3 border-top" v-if="stepItem.active && selectedActiveStep && allowEdit && checkPermission(permission)">
                    <ValidationObserver ref="updateTimelineForm">
                        <div class="d-flex align-items-end flex-wrap">
                            <div class="mr-3 mb-3" style="min-width: 250px; flex: 1; max-width: 400px;">
                                <ValidationProvider name="status" rules="required" v-slot="{ errors }">
                                    <b-form-group :label="$t('status')" class="mb-0">
                                        <div>
                                            <multiselect
                                                :placeholder="$t('select')"
                                                v-model="localStatus"
                                                :options="statusOptions"
                                                :custom-label="statusCustomLabel"
                                                :select-label="''"
                                                :selected-label="''"
                                                :deselect-label="''"
                                                :searchable="false"
                                            />
                                            <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                        </div>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>
                            <div class="mb-3">
                                <b-button
                                    variant="success"
                                    class="py-2 px-4 font-weight-bold d-flex align-items-center"
                                    style="height: 38px; line-height: 1;"
                                    :disabled="isLoading"
                                    @click="triggerSave"
                                >
                                    <b-spinner small class="mr-2" v-if="isLoading" />
                                    {{ $t('save').toUpperCase() }}
                                </b-button>
                            </div>
                        </div>

                        <div class="row mt-2" v-if="localStatus === 'declined'">
                            <div class="col-12">
                                <ValidationProvider name="explanation" rules="required" v-slot="{ errors }">
                                    <b-form-group :label="$t('explanation')">
                                        <b-form-textarea
                                            no-resize
                                            rows="3"
                                            maxlength="100"
                                            v-model="localReason"
                                        />
                                        <div class="invalid-feedback d-block" v-if="errors[0]" v-html="errors[0]"></div>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>

                <!-- Dynamic Form Slot for Active Step (only shown when approved) -->
                <div class="mt-3 pt-3 border-top" v-if="stepItem.active && localStatus === 'approved'">
                    <slot :name="'step-' + stepItem.rank" :step="stepItem"></slot>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
    name: "ApprovalTimeline",
    components: {
        ValidationObserver,
        ValidationProvider
    },
    props: {
        steps: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedActiveStep: {
            type: Boolean,
            required: true,
            default: false
        },
        allowEdit: {
            type: Boolean,
            required: true,
            default: false
        },
        isLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        permission: {
            type: String,
            required: false,
            default: "approvalstep_updatestatus"
        },
        status: {
            type: String,
            default: null
        },
        reason: {
            type: String,
            default: null
        },
        statuses: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        localStatus: {
            get() { return this.status; },
            set(val) { this.$emit('update:status', val); }
        },
        localReason: {
            get() { return this.reason; },
            set(val) { this.$emit('update:reason', val); }
        },
        statusOptions() {
            let options = [];
            if (this.statuses) {
                for (const item in this.statuses) {
                    options.push(item);
                }
            }
            return options;
        }
    },
    methods: {
        getBadgeVariant(status) {
            switch(status) {
                case 'approved':
                case 'completed':
                    return 'success';
                case 'declined':
                case 'rejected':
                    return 'danger';
                case 'pending':
                case 'waiting_requester_review':
                case 'waiting_cfo_or_president_review':
                case 'waiting_po_review':
                case 'waiting_po_approval':
                    return 'warning';
                default:
                    return 'secondary';
            }
        },
        getTimelineIcon(status) {
            switch(status) {
                case 'approved':
                case 'completed':
                    return 'ri-checkbox-circle-fill';
                case 'declined':
                case 'rejected':
                    return 'ri-close-circle-fill';
                default:
                    return 'ri-checkbox-blank-circle-fill';
            }
        },
        statusCustomLabel(key) {
            return this.getLocaleText(this.statuses?.[key], 'name') || key || '';
        },
        triggerSave() {
            const form = Array.isArray(this.$refs.updateTimelineForm)
                ? this.$refs.updateTimelineForm[0]
                : this.$refs.updateTimelineForm;
            if (!form) {
                this.$emit('save');
                return;
            }
            form.validate().then(valid => {
                if (valid) {
                    this.$emit('save');
                }
            });
        }
    }
}
</script>

<style scoped>
.font-weight-bold {
    font-weight: 600 !important;
}
.modern-timeline {
    position: relative;
    padding-left: 10px;
}
.timeline-node {
    display: flex;
    position: relative;
}
.node-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 20px;
}
.node-indicator {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    color: white;
    font-size: 1.1rem;
}
.node-indicator.approved, .node-indicator.completed {
    background-color: #28c76f;
}
.node-indicator.declined, .node-indicator.rejected {
    background-color: #ea5455;
}
.node-indicator.pending, .node-indicator.waiting_student_approve {
    background-color: #ff9f43;
}
.node-indicator.bg-primary-theme {
    background-color: rgb(14, 177, 249) !important;
}
.border-primary-theme {
    border: 1px solid rgb(14, 177, 249) !important;
    border-left: 4px solid rgb(14, 177, 249) !important;
}
.node-line {
    width: 2px;
    flex-grow: 1;
    background-color: #e2e8f0;
    margin: 5px 0;
}
.node-right {
    flex-grow: 1;
    background-color: #fff;
    transition: all 0.2s ease;
}
.node-right:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}
.node-rank {
    font-size: 0.75rem;
    font-weight: 700;
    color: #8c9ba5;
    text-transform: uppercase;
}
.node-title {
    color: #1e293b;
    font-weight: 700;
}
.border-left-danger {
    border-left: 3px solid #dc3545;
}
</style>
